import type {
    GetDropdownValuesRequest,
    Order,
} from "@amzn/d16-g-client-deck-builder-typescript-client";
import { useDebouncedEffect, useAsync } from "@react-hookz/web";
import React, { useMemo, useState, useEffect, useRef } from "react";
import { useStore } from "zustand";

import { getCdbApiClient } from "../../api/cdbApi/cdbApi";
import type { DropdownComponentProps } from "../../components/common/Dropdown";
import { DropdownComponent } from "../../components/common/Dropdown";
import type { DropdownItemData } from "../../components/common/DropdownHelpers";
import { CLIENT_ID } from "../../config/constants";
import {
    useClearDimensionValueOnDependencyChange,
    useGetDimensionDisable,
    useSetDimensionValue,
    useUpdateStatusOnChange,
} from "../../hooks/dimension/dimension.hooks";
import { ctiStore } from "../../store/cti";
import {
    dimensionHydrationStore,
    HydrationState,
    useDimensionHydration,
} from "../../store/dimension.hydration";
import { dimensionStatusStore } from "../../store/dimension.status";
import type { DropdownDimensionValue } from "../../store/dimension.value";
import { dimensionValueStore } from "../../store/dimension.value";
import { NotificationType, notificationStore } from "../../store/notification";
import type { InputParameterFilter } from "../../types/common";
import { Comparator } from "../../types/common";
import type {
    DimensionConfiguration,
    DimensionTemplate,
    DimensionTypes,
} from "../../types/dimensions";
import { isLoading } from "../../utils/api.utils";
import {
    buildRequestParameter,
    buildQueryParameterFilter,
} from "../../utils/app.utils";
import { buildMissingTextWithSimTemplate } from "../../utils/cti.utils";

import * as DropdownDimensionHelpers from "./DropdownDimensionHelpers";
import { BasicDimensionBox } from "./common/components";

export interface DropdownDimensionProps extends DimensionTemplate {
    type: DimensionTypes.DROPDOWN;
    configuration: DropdownDimensionConfiguration;
}

export interface DropdownDimensionConfiguration extends DimensionConfiguration {
    inputParameterFilters?: InputParameterFilter[];
    inputSource: string;
    itemMapper: DropdownDimensionHelpers.ItemMapper;
    missingText?: string;
    orderBy?: Order[];
}

export const DropdownDimension: React.FunctionComponent<
    DropdownDimensionProps
> = (props) => {
    const {
        dependencies,
        id,
        configuration,
        optional = false,
        requestParameterMap,
        dimensionMetadata,
    } = props;
    const {
        inputParameterFilters = [],
        inputSource,
        isAdvertiserScoped,
        itemMapper,
        label,
        missingText,
        orderBy,
        tooltip,
    } = configuration;

    const dependencyValues = useStore(dimensionValueStore, (state) =>
        dependencies.map((d) => state[d]?.data),
    );

    const lastApiStartTime = useRef(0);
    const [isItemsFromHydration, setIsItemsFromHydration] =
        React.useState(false);

    const [dependencyAllSuccess] = useStore(dimensionStatusStore, (state) => [
        state.checkStatusSuccess(dependencies),
    ]);

    const hydrationStatus = useStore(
        dimensionHydrationStore,
        (state) => state.dimensionHydrationStatus[id],
    );

    const [showItemsWhileLoading, setShowItemsWhileLoading] = useState(false);

    const [upsertNotification] = useStore(notificationStore, (state) => [
        state.upsertNotification,
    ]);

    const [ctiState] = useStore(ctiStore, (state) => [state]);

    const [filter, setFilter] = useState("");
    const [pageIndex, setPageIndex] = useState(0);
    const [items, setItems] = useState<DropdownItemData[]>([]);
    const maxResultsResponse = useRef(Number.MAX_VALUE);
    const nextTokenResponse = useRef<number | undefined>(undefined);

    // build query parameters
    const queryParameter = useMemo(
        () => `${Object.values(itemMapper).join(",")}`,
        [],
    );

    const rpMap = useMemo(
        () =>
            requestParameterMap ||
            DropdownDimensionHelpers.defaultRequestParameterMapping(id),
        [],
    );

    const [dimensionData, setDimensionValue] =
        useSetDimensionValue<DropdownDimensionValue>(
            id,
            buildRequestParameter(rpMap),
        );

    const dropdownFilterQueryParameter = useMemo(
        () =>
            DropdownDimensionHelpers.getDropdownFilters(
                inputParameterFilters,
                filter.length > 0
                    ? buildQueryParameterFilter(
                          itemMapper.label,
                          Comparator.ILIKE,
                          `%${filter}%`,
                      )
                    : undefined,
                isAdvertiserScoped,
            ),
        [filter, ...dependencyValues],
    );

    const fetchItemsFromApi = async (pageIndex: number) => {
        const body: GetDropdownValuesRequest = {
            datasetName: inputSource,
            columnNames: queryParameter,
            additionalFilters: dropdownFilterQueryParameter,
            pageToken: pageIndex,
            pageSize: 50,
            orderBy,
            metadata: {
                dimensionIdentifier: id,
                domainIdentifier: dimensionMetadata?.domainIdentifier,
                clientId: CLIENT_ID,
            },
        };

        const { data, maxResults, nextToken } = await (
            await getCdbApiClient()
        ).getDropdownValues(body);

        const fetchData = DropdownDimensionHelpers.processItemMapper(
            data ?? [],
            itemMapper,
        );

        return {
            items: fetchData,
            maxItems: maxResults ?? 0,
            nextToken,
        };
    };

    const [inputRequestState, inputRequestAction] = useAsync(async () => {
        try {
            const execution = new Date().valueOf();
            if (execution < lastApiStartTime.current) {
                return null;
            }

            if (isItemsFromHydration) {
                setIsItemsFromHydration(false);
                return null;
            }
            const {
                items: fetchData,
                maxItems,
                nextToken,
            } = await fetchItemsFromApi(pageIndex);
            maxResultsResponse.current = maxItems ?? 0;
            nextTokenResponse.current = nextToken;
            if (pageIndex === 0) {
                setItems(fetchData);
                // auto select item if only one is returned
                if (
                    !optional &&
                    filter.length === 0 &&
                    fetchData.length === 1
                ) {
                    setDimensionValue(fetchData[0]);
                }
            } else {
                setItems([...items, ...fetchData]);
            }
            setShowItemsWhileLoading(false);
            lastApiStartTime.current = execution;
        } catch (err: any) {
            upsertNotification(err.message, NotificationType.ERROR);
        }
    });

    useDebouncedEffect(
        () =>
            void (async () => {
                if (
                    dependencyAllSuccess &&
                    hydrationStatus !== HydrationState.PENDING
                )
                    await inputRequestAction.execute();
            })(),
        [
            pageIndex,
            dropdownFilterQueryParameter,
            hydrationStatus,
            dependencyAllSuccess,
        ],
        300,
    );

    // clears page index when dependency values change
    useEffect(() => {
        setItems([]);
        setPageIndex(0);
        nextTokenResponse.current = undefined;
    }, [dropdownFilterQueryParameter]);

    // clears page index if filter changes
    useEffect(() => {
        setPageIndex(0);
        setItems([]);
        nextTokenResponse.current = undefined;
    }, [filter]);

    const disabled = useGetDimensionDisable(dependencies);

    useClearDimensionValueOnDependencyChange(id, dependencies);

    // set status
    useUpdateStatusOnChange(id, (value: DropdownDimensionValue) =>
        DropdownDimensionHelpers.processDimensionStatus(value, optional),
    );

    const missingTextWithSimTemplate: string | undefined = useMemo(() => {
        const templateId = ctiState.cti?.simTTemplateId;
        return buildMissingTextWithSimTemplate(label, templateId);
    }, [ctiState.cti]);

    useDimensionHydration(id, dependencies, async (hydrationPayload) => {
        const payloadValues = {
            valueLookupKey: (() => {
                if (requestParameterMap) {
                    return requestParameterMap.value;
                } else {
                    return `${id}_id`;
                }
            })(),
        };
        if (!hydrationPayload[payloadValues.valueLookupKey] && !optional) {
            throw new Error("could not locate value, label in payload");
        }

        let hydrationItems: DropdownItemData[] = [];
        let hydrationPageIndex = 0;
        let hydrationMaxItems = Infinity;

        while (hydrationItems.length < hydrationMaxItems) {
            const {
                items: fetchData,
                maxItems,
                nextToken,
            } = await fetchItemsFromApi(hydrationPageIndex);
            hydrationItems = hydrationItems.concat(fetchData);
            hydrationMaxItems = maxItems;
            const item = hydrationItems.find(
                (e) =>
                    e.value === hydrationPayload[payloadValues.valueLookupKey],
            );
            if (item) {
                setIsItemsFromHydration(true);
                setItems(hydrationItems);
                setDimensionValue(item);
                nextTokenResponse.current = nextToken;
                return;
            }
            if (nextToken === undefined || nextToken <= hydrationPageIndex) {
                break;
            }
            hydrationPageIndex++;
        }

        if (!optional) {
            throw new Error("could not find item matching payload value");
        }
    });

    const dropdownProps: DropdownComponentProps = {
        dimension: id,
        label,
        tooltip,
        items,
        search: filter,
        onChange: (e) => {
            const value: DropdownDimensionValue = items.find(
                (item) => item.value === e,
            ) || {
                value: e,
                label: e,
            };
            setDimensionValue(value);
        },
        selectedValue: dimensionData.value,
        setSearch: (e) => {
            inputRequestState.status = "loading";
            setFilter(e);
        },
        onEndScroll: () => {
            if (
                nextTokenResponse.current !== undefined &&
                nextTokenResponse.current > pageIndex
            ) {
                setShowItemsWhileLoading(true);
                inputRequestState.status = "loading";
                setPageIndex(pageIndex + 1);
            }
        },
        loading: isLoading(inputRequestState.status),
        showItemsWhileLoading,
        disabled,
        optional,
        missingText: missingText ?? missingTextWithSimTemplate,
    };

    return (
        <BasicDimensionBox>
            <DropdownComponent {...dropdownProps} />
        </BasicDimensionBox>
    );
};

export default DropdownDimension;
